import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui';
import Input from '@/components/ui/Input/Input';
import { WebsitePageSection } from '@/types';
import { showToast } from '@/lib/toast';
import { ContentWithCredentialsFormData, ContentWithCredentialsFormDataSchema } from '@/components/Modals/EditContentsModals/EditContentsSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import { parsePhoneNumber } from 'libphonenumber-js';

interface InlineCredentialsEditorProps {
  data: WebsitePageSection;
  onSave: (updatedData: Partial<WebsitePageSection>) => void;
  onCancel: () => void;
}

const InlineCredentialsEditor: React.FC<InlineCredentialsEditorProps> = ({ data, onSave, onCancel }) => {
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: zodResolver(ContentWithCredentialsFormDataSchema),
    defaultValues: {
      title: data.title ?? '',
      emailAddress: data.emailAddress ?? '',
      address: data.address ?? '',
      phoneNumber: data.phoneNumber ?? ''
    },
    mode: 'all'
  });

  useEffect(() => {
    reset({
      title: data.title ?? '',
      emailAddress: data.emailAddress ?? '',
      address: data.address ?? '',
      phoneNumber: data.phoneNumber ?? ''
    });
  }, [data, reset]);

  const onSubmit = async (formData: ContentWithCredentialsFormData) => {
    if (!data.sectionTag) {
      showToast.error('Section tag not found.');
      return;
    }

    try {
      setIsLoading(true);
      
      const parsedPhoneNumber = parsePhoneNumber(String(formData.phoneNumber ?? ''));

      onSave({
        title: formData.title ?? '',
        emailAddress: formData.emailAddress ?? '',
        address: formData.address ?? '',
        phoneCountryCode: parsedPhoneNumber?.countryCallingCode ?? '',
        phoneNumber: parsedPhoneNumber?.number ?? '',
        contentType: 'WITH_CREDENTIALS'
      });
    } catch (error) {
      setIsLoading(false);
      showToast.error('An unexpected error occurred.');
      console.error('Update failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Controller
          name="title"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Title"
              label="Title"
              error={errors.title?.message}
              $dataId="inline-credentials-title-input"
              $bgColor="var(--inputBgColor)"
            />
          )}
        />

        <Controller
          name="emailAddress"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              type="email"
              placeholder="Email Address"
              label="Email Address"
              error={errors.emailAddress?.message}
              $dataId="inline-credentials-email-input"
              $bgColor="var(--inputBgColor)"
            />
          )}
        />

        <Controller
          name="phoneNumber"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              type="tel"
              placeholder="Phone Number"
              label="Phone Number"
              error={errors.phoneNumber?.message}
              $dataId="inline-credentials-phone-input"
              $bgColor="var(--inputBgColor)"
            />
          )}
        />

        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              type="text"
              placeholder="Address"
              label="Address"
              error={errors.address?.message}
              $dataId="inline-credentials-address-input"
              $bgColor="var(--inputBgColor)"
            />
          )}
        />
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={isLoading}
          disabled={isLoading}
        >
          Save
        </Button>
      </div>
    </form>
  );
};

export default InlineCredentialsEditor;

